import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicDishAppData } from '../api/api_dishes';
import { buildStatistics } from '../components/ParametersGroups.jsx';
import { useTranslation } from '../hooks/useTranslation';
import { calculateEnergyFromMacros } from '../utils/energyCalculation';
import { getDishAverages } from '../utils/sustainabilityAverages';
import EthicsShow from '../components/EthicsShow.jsx';
import CO2eShowComponent from '../components/CO2eShowComponent.jsx';
import MobileBottomSheet from '../components/MobileBottomSheet.jsx';
import PublicDishCircleProgressChart from '../components/PublicDishCircleProgressChart.jsx';
import ProgressBarComponent from '../components/ProgressBarComponent.jsx';
import { publicDishPdfTexts } from '../components/PublicDishParameterText.js';
import DishParameterCard from '../components/Cards/DishParameterCard.jsx';

const safeText = (val) => (typeof val === 'string' ? val : val == null ? '' : String(val));

const SHARE_TOKEN_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Fixed “phone” column width on all viewports (≈ iPhone 14 Pro Max CSS width). */
const PUBLIC_DISH_MOBILE_MAX_CLASS = 'max-w-[430px]';

const TRANSPARENCY_SCORE = 100;

const transparencyStatistic = {
  title: 'Transparens',
  categories: [
    {
      name: 'Transparens',
      engName: 'transparency',
      score: TRANSPARENCY_SCORE,
      bonus: null,
    },
  ],
};

const TEXT_KEY_BY_PARAM_NAME = {
  Klimat: 'climate',
  Vatten: 'water',
  'Biologisk mångfald': 'biodiversity',
  Transport: 'transport',
  Landanvändning: 'landUse',
  Bekämpningsmedel: 'pesticides',
  Övergödning: 'fertilizers',
  Hälsa: 'nutritionalValue',
  Antibiotikaresistens: 'antibiotics',
  'Social rättvisa': 'socialJustice',
  Barnarbete: 'childLabor',
  Djurvälfärd: 'animalWelfare',
  Arbetsförhållanden: 'workersRights',
  Jämlikhet: 'equality',
  Lokalbefolkning: 'local_population',
  Transparens: 'transparancy',
};

const getPdfTextKeyForParamName = (name) => {
  const key = safeText(name).trim();
  return TEXT_KEY_BY_PARAM_NAME[key] || null;
};

const toBonusNumber = (bonus) => {
  if (bonus == null) return 0;
  if (typeof bonus === 'number' && Number.isFinite(bonus)) return bonus;
  if (typeof bonus === 'object' && Number.isFinite(bonus?.value)) return bonus.value;
  const parsed = Number(bonus);
  return Number.isFinite(parsed) ? parsed : 0;
};

const CategorySheetBody = ({ statistic, averageScore, healthIntro, transparencyIntro, t, onSelectParam }) => {
  if (!statistic) return null;
  return (
    <>
      <div className="flex justify-center -my-2 w-full min-w-0 overflow-x-hidden">
        <PublicDishCircleProgressChart
          name={statistic.title}
          score={averageScore}
          strokeWidth={14}
          originalSize={260}
          originalRadius={90}
          reductionFactor={0.88}
          centerContentInsetRatio={0.17}
          t={t}
        />
      </div>
      {transparencyIntro ? (
        <span className="block text-sm font-bold text-gray-700 px-1 mb-2 leading-snug">
          {t('Full transparens: all tillgänglig information om rätten visas för dig.')}
        </span>
      ) : healthIntro ? (
        <span className="block text-sm font-bold text-gray-700 px-1 mb-2 leading-snug">
          {t('Hälsopoängen baseras enbart på Hälsa:')}
        </span>
      ) : (
        <span className="block text-sm font-bold text-gray-700 px-1 mb-2 leading-snug">
          {t('I denna kategori ingår följande parametrar:')}
        </span>
      )}
      <div className="space-y-0.5">
        {statistic.categories.map((cat) => {
          const disabled = cat?.score == null;
          const handleClick = () => {
            if (disabled) return;
            const pdfKey = getPdfTextKeyForParamName(cat?.name);
            const texts = (pdfKey && publicDishPdfTexts[pdfKey]) ? publicDishPdfTexts[pdfKey] : null;
            const bonusVal = toBonusNumber(cat?.bonus);
            const base = typeof cat?.score === 'number' ? cat.score : null;
            const finalScore = typeof base === 'number' ? Math.min(base + bonusVal, 100) : null;
            onSelectParam?.({
              name: cat?.name,
              score: base,
              bonus: cat?.bonus ?? null,
              finalScore,
              texts,
            });
          };
          return (
            <button
              key={`${cat.engName}-${cat.name}`}
              type="button"
              disabled={disabled}
              onClick={handleClick}
              className={`w-full text-left ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            >
              <ProgressBarComponent
                name={cat.name}
                score={cat.score}
                bonus={cat.bonus}
                isIcon
                t={t}
              />
            </button>
          );
        })}
      </div>
    </>
  );
};

const PublicDishInfoPage = () => {
  const { t } = useTranslation();
  const { shareToken } = useParams();

  const [dish, setDish] = useState(null);
  const [nutrients, setNutrients] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSheet, setActiveSheet] = useState(null);
  const [activeParamSheet, setActiveParamSheet] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');

    if (!shareToken || !SHARE_TOKEN_UUID_RE.test(shareToken)) {
      setError(t('Ogiltig eller utgången länk'));
      setLoading(false);
      return () => {
        alive = false;
      };
    }

    fetchPublicDishAppData(shareToken)
      .then((res) => {
        if (!alive) return;
        const body = res?.data;
        if (!body?.success || !body?.dish) {
          throw new Error(body?.message || 'Not found');
        }
        setDish(body.dish);
        setNutrients(null);
      })
      .catch((e) => {
        if (!alive) return;
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          t('Kunde inte ladda rätten');
        setError(msg);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [shareToken, t]);

  const title = useMemo(() => {
    return safeText(
      dish?.name || dish?.title || `${t('Rätt')} ···${shareToken?.slice(-6) || ''}`
    );
  }, [dish, shareToken, t]);

  const imageUrl = dish?.image_url || dish?.image || dish?.imageUrl || '';
  const allergens = Array.isArray(dish?.allergens) ? dish.allergens : null;
  const ingredientsText = safeText(dish?.original_contents || dish?.ingredients || '');
  const affiliateName =
    safeText(
      dish?.affiliate?.name ||
        dish?.affiliate_name ||
        dish?.creator?.name ||
        dish?.owner?.name
    ) || '';

  const normalizedNutrients = useMemo(() => {
    if (Array.isArray(dish?.nutrients) && dish.nutrients.length > 0) {
      const arr = dish.nutrients;
      const byName = (names) => {
        const wanted = new Set(names.map((x) => x.toLowerCase()));
        const hit = arr.find((n) => wanted.has(String(n?.name || '').trim().toLowerCase()));
        if (!hit) return null;
        const v = Number(hit.amount_per_100g);
        return {
          value: Number.isFinite(v) ? v : null,
          unit: hit.unit_abbr || hit.unit || '',
        };
      };
      const kcal = (() => {
        const calc = calculateEnergyFromMacros(arr, false);
        if (Number.isFinite(calc) && calc > 0) {
          return { value: Math.ceil(calc), unit: 'kcal' };
        }
        const fallback = byName(['energi', 'energy', 'kilokalorier', 'kcal']);
        if (fallback?.value != null && Number.isFinite(fallback.value)) {
          return { ...fallback, value: Math.ceil(fallback.value) };
        }
        return fallback;
      })();
      return {
        kcal,
        protein: byName(['protein']),
        fat: byName(['fett', 'fat']),
        carbs: byName(['kolhydrater', 'carbohydrates', 'carbs']),
      };
    }

    const n = nutrients;
    if (!n || typeof n !== 'object') return null;
    const root = n?.nutrients && typeof n.nutrients === 'object' ? n.nutrients : n;
    const wrap = (value, unit) => ({
      value: value == null ? null : Number(value),
      unit: unit || '',
    });
    return {
      kcal: wrap(root?.kcal ?? root?.energy_kcal ?? root?.energy ?? root?.calories ?? null, 'kcal'),
      protein: wrap(root?.protein ?? root?.protein_g ?? null, 'g'),
      fat: wrap(root?.fat ?? root?.fat_g ?? root?.total_fat ?? null, 'g'),
      carbs: wrap(root?.carbs ?? root?.carbohydrates ?? root?.carbohydrates_g ?? null, 'g'),
    };
  }, [dish?.nutrients, nutrients]);

  const statistics = useMemo(() => (dish ? buildStatistics(dish) : []), [dish]);
  const dishAvgs = useMemo(() => getDishAverages(dish), [dish]);

  const [openIngredients, setOpenIngredients] = useState(false);
  const [openNutrition, setOpenNutrition] = useState(false);
  const [openAbout, setOpenAbout] = useState(false);

  const sheetTitle = useMemo(() => {
    if (!activeSheet) return '';
    if (activeSheet === 'transparency') return t('Transparens');
    const map = { environment: 'Miljö', health: 'Hälsa', social: 'Rättvisa' };
    return t(map[activeSheet] || '');
  }, [activeSheet, t]);

  const sheetBody = useMemo(() => {
    if (!dish || !activeSheet) return null;
    if (activeSheet === 'transparency') {
      return (
        <CategorySheetBody
          statistic={transparencyStatistic}
          averageScore={TRANSPARENCY_SCORE}
          healthIntro={false}
          transparencyIntro
          t={t}
          onSelectParam={(p) => setActiveParamSheet(p)}
        />
      );
    }
    const idx =
      activeSheet === 'environment' ? 0 : activeSheet === 'health' ? 1 : activeSheet === 'social' ? 2 : -1;
    const statistic = statistics[idx];
    if (!statistic) return null;
    const averageScore =
      activeSheet === 'environment'
        ? dishAvgs.environment_average
        : activeSheet === 'health'
          ? dishAvgs.health_average
          : dishAvgs.social_average;
    return (
      <CategorySheetBody
        statistic={statistic}
        averageScore={typeof averageScore === 'number' ? averageScore : 0}
        healthIntro={activeSheet === 'health'}
        t={t}
        onSelectParam={(p) => setActiveParamSheet(p)}
      />
    );
  }, [activeSheet, dish, statistics, dishAvgs, t]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-neutral-300">
        <div
          className={`mx-auto w-full ${PUBLIC_DISH_MOBILE_MAX_CLASS} min-h-[100dvh] bg-neutral-100 px-4 py-8 laptop:shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_12px_40px_rgba(0,0,0,0.08)]`}
        >
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="h-6 w-44 bg-gray-200 rounded animate-pulse" />
            <div className="mt-4 h-48 bg-gray-100 rounded-2xl animate-pulse" />
            <div className="mt-6 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-full animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dish) {
    return (
      <div className="min-h-[100dvh] bg-neutral-300">
        <div
          className={`mx-auto w-full ${PUBLIC_DISH_MOBILE_MAX_CLASS} min-h-[100dvh] bg-neutral-100 px-4 py-10 laptop:shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_12px_40px_rgba(0,0,0,0.08)]`}
        >
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="text-lg font-bold text-gray-900">{t('Kunde inte ladda rätten')}</div>
            <div className="mt-2 text-sm text-gray-600">{error || t('Okänt fel')}</div>
            <div className="mt-6">
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 rounded-xl bg-secondary-green text-white font-semibold"
              >
                {t('Tillbaka')}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const envScore = dishAvgs.environment_average;
  const healthScore = dishAvgs.health_average;
  const socialScore = dishAvgs.social_average;

  return (
    <div className="min-h-[100dvh] bg-neutral-300">
      <div
        className={`mx-auto w-full ${PUBLIC_DISH_MOBILE_MAX_CLASS} min-h-[100dvh] flex flex-col pb-8 bg-neutral-100 laptop:shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_12px_40px_rgba(0,0,0,0.08)]`}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-white border-b border-gray-100">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) window.history.back();
              else window.location.href = '/';
            }}
            className="text-sm font-semibold text-gray-700 hover:text-gray-900 flex-shrink-0"
          >
            ← {t('Tillbaka')}
          </button>
          {affiliateName ? (
            <div className="text-xs font-semibold text-gray-600 truncate text-right">{affiliateName}</div>
          ) : (
            <span />
          )}
        </div>

        <div className="bg-white px-5 pt-5 pb-6">
          <div className="flex gap-4 items-start">
            {imageUrl ? (
              <div className="w-[32%] max-w-[140px] flex-shrink-0 rounded-xl overflow-hidden border border-gray-200 bg-gray-50 aspect-[3/4] max-h-52">
                <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-[32%] max-w-[140px] flex-shrink-0 rounded-xl border border-gray-200 bg-gray-50 aspect-[3/4] max-h-52 flex items-center justify-center text-[10px] text-gray-400 font-semibold text-center px-1">
                {t('Ingen bild')}
              </div>
            )}
            <div className="min-w-0 flex-1 pt-1">
              <h1 className="text-2xl font-extrabold text-gray-900 leading-tight m-0 break-words">{title}</h1>
              {dish?.size != null && String(dish.size).trim() !== '' ? (
                <p className="text-sm text-gray-600 mt-1 m-0">
                  {dish.size}
                  {dish?.unit_abbr ? ` ${dish.unit_abbr}` : ''}
                </p>
              ) : null}
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                <EthicsShow
                  isAnimal={!!dish?.is_animal}
                  isAnimalKilled={!!dish?.is_animal_killed}
                  isVegan={!!dish?.is_vegan}
                  isVegetarian={!!dish?.is_vegetarian}
                  ethicsUnknownConfirmed={!!dish?.ethics_unknown_confirmed}
                  size="large"
                  showLabels
                />
              </div>
              {affiliateName ? (
                <p className="text-sm text-gray-500 mt-2 m-0 truncate">{affiliateName}</p>
              ) : null}
            </div>
          </div>

          <div className="mt-5 rounded-2xl p-0">
            <CO2eShowComponent
              co2ePerKg={Number(dish?.CO2e_menuwise ?? dish?.co2e_equivalent ?? dish?.CO2e ?? dish?.co2e ?? 0)}
              size={Number(dish?.size ?? 0)}
              unit_id={Number(dish?.unit_id ?? 0)}
              portionSize={dish?.portion_size}
              fullWidth
            />
          </div>
        </div>

        <div className="flex-1 px-3 tablet:px-5 pt-4 w-full min-w-0 max-w-full overflow-x-hidden box-border">
          <p className="text-sm text-gray-600 leading-snug m-0 mb-4">
            {t(
              'Höga värden indikerar att rätten är mer hållbar än en rätt med lägre värden.'
            )}
          </p>

          <div className="grid grid-cols-2 gap-2 tablet:gap-3 w-full min-w-0 max-w-full">
            <DishParameterCard
              title={t("Miljö")}
              iconSrc="/assets/icons/svg/Miljo.svg"
              score={typeof envScore === 'number' && envScore > 0 ? envScore : null}
              disabled={!(typeof envScore === 'number' && envScore > 0)}
              onClick={() => setActiveSheet('environment')}
              t={t}
            />
            <DishParameterCard
              title={t("Hälsa")}
              iconSrc="/assets/icons/svg/Halsa.svg"
              score={typeof healthScore === 'number' && healthScore > 0 ? healthScore : null}
              disabled={!(typeof healthScore === 'number' && healthScore > 0)}
              onClick={() => setActiveSheet('health')}
              t={t}
            />
            <DishParameterCard
              title={t("Rättvisa")}
              iconSrc="/assets/icons/svg/Rattvisa.svg"
              score={typeof socialScore === 'number' && socialScore > 0 ? socialScore : null}
              disabled={!(typeof socialScore === 'number' && socialScore > 0)}
              onClick={() => setActiveSheet('social')}
              t={t}
            />
            <DishParameterCard
              title="Transparens"
              iconSrc="/assets/icons/svg/Visa.svg"
              score={TRANSPARENCY_SCORE}
              disabled={false}
              onClick={() => setActiveSheet('transparency')}
              t={t}
            />
          </div>

          {allergens && allergens.length > 0 ? (
            <div className="mt-6">
              <div className="text-xs font-extrabold text-gray-600 uppercase tracking-wide">{t('Allergener')}</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {allergens.map((a) => (
                  <span
                    key={String(a)}
                    className="px-3 py-1 rounded-full bg-neutral-200/80 text-gray-800 text-sm font-semibold"
                  >
                    {t(String(a))}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="mt-6 space-y-3">
            {ingredientsText ? (
              <div className="rounded-xl bg-neutral-200/60 px-4 border border-neutral-300/30">
                <button
                  type="button"
                  onClick={() => setOpenIngredients((v) => !v)}
                  className="w-full py-4 flex items-center justify-between text-left"
                >
                  <span className="text-base font-semibold text-gray-800">{t('Ingredienser')}</span>
                  <img
                    src={openIngredients ? '/assets/icons/Dolj.png' : '/assets/icons/svg/Visa.svg'}
                    alt=""
                    className="w-5 h-5 opacity-70"
                  />
                </button>
                {openIngredients ? (
                  <div className="px-0 pb-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed border-t border-neutral-300/30 pt-3">
                    {ingredientsText}
                  </div>
                ) : null}
              </div>
            ) : null}

            {normalizedNutrients ? (
              <div className="rounded-xl bg-neutral-200/60 px-4 border border-neutral-300/30">
                <button
                  type="button"
                  onClick={() => setOpenNutrition((v) => !v)}
                  className="w-full py-4 flex items-center justify-between text-left"
                >
                  <span className="text-base font-semibold text-gray-800">{t('Näringsvärde')}</span>
                  <img
                    src={openNutrition ? '/assets/icons/Dolj.png' : '/assets/icons/svg/Visa.svg'}
                    alt=""
                    className="w-5 h-5 opacity-70"
                  />
                </button>
                {openNutrition ? (
                  <div className="px-0 pb-4 border-t border-neutral-300/30 pt-3">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        ['kcal', t('Energi')],
                        ['protein', t('Protein')],
                        ['fat', t('Fett')],
                        ['carbs', t('Kolhydrater')],
                      ].map(([key, label]) => (
                        <div key={key} className="rounded-2xl bg-white/70 border border-gray-100 p-3">
                          <div className="text-xs font-bold text-gray-600">{label}</div>
                          <div className="mt-1 text-lg font-extrabold text-gray-900">
                            {(() => {
                              const entry = normalizedNutrients?.[key];
                              const v = entry?.value;
                              if (!Number.isFinite(v)) return '-';
                              const unit = entry?.unit ? ` ${entry.unit}` : '';
                              return `${v.toFixed(2)}${unit}`;
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="rounded-xl bg-neutral-200/60 px-4 border border-neutral-300/30">
              <button
                type="button"
                onClick={() => setOpenAbout((v) => !v)}
                className="w-full py-4 flex items-center justify-between text-left"
              >
                <span className="text-base font-semibold text-gray-800">{t('Om poängen')}</span>
                <img
                  src={openAbout ? '/assets/icons/Dolj.png' : '/assets/icons/svg/Visa.svg'}
                  alt=""
                  className="w-5 h-5 opacity-70"
                />
              </button>
              {openAbout ? (
                <div className="px-0 pb-4 text-sm text-gray-700 leading-relaxed border-t border-neutral-300/30 pt-3 space-y-2">
                  <p className="m-0">
                    {t(
                      'Poängen bygger på tillgänglig data om rätten och dess ingredienser. Saknas data visas ingen siffra (•).'
                    )}
                  </p>
                  <p className="m-0">
                    {t('Transparens visas som 100 % eftersom du ser all data vi kan dela om rätten.')}
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          <p className="mt-8 text-xs text-gray-500 text-center">{t('Skannad via QR-kod')}.</p>
        </div>
      </div>

      <MobileBottomSheet
        open={!!activeSheet}
        onOpenChange={(open) => {
          if (!open) setActiveSheet(null);
        }}
        title={sheetTitle}
        zIndexClass="z-[100]"
      >
        {sheetBody}
      </MobileBottomSheet>

      <MobileBottomSheet
        open={!!activeParamSheet}
        onOpenChange={(open) => {
          if (!open) setActiveParamSheet(null);
        }}
        title={activeParamSheet?.name ? t(String(activeParamSheet.name)) : ''}
        zIndexClass="z-[110]"
      >
        <div className="space-y-5">
          <div className="rounded-2xl bg-neutral-200/75 px-4 py-4">
            <p className="text-xl font-semibold text-gray-900 m-0">
              {(() => {
                const title = activeParamSheet?.texts?.title || activeParamSheet?.name || '';
                const scoreDisplay =
                  typeof activeParamSheet?.finalScore === 'number'
                    ? Math.round(activeParamSheet.finalScore)
                    : '•';
                return `${title} ${scoreDisplay} av 100`;
              })()}
            </p>
          </div>

          <div className="rounded-2xl bg-neutral-200/75 px-4 py-4">
            <div className="text-sm text-secondary leading-relaxed">
              {(() => {
                const issue = activeParamSheet?.texts?.issueTextParagraphs || [];
                const how = activeParamSheet?.texts?.howToThinkTextParagraphs || [];
                const fourth = activeParamSheet?.texts?.fourthtext || [];
                const fourthArr = Array.isArray(fourth) ? fourth : (fourth ? [fourth] : []);
                const paragraphs = [...issue, ...how, ...fourthArr].filter((p) => safeText(p).trim() !== '');
                if (paragraphs.length === 0) {
                  return <p className="m-0 text-gray-700">{t('Ingen beskrivning tillgänglig.')}</p>;
                }
                return paragraphs.map((p, i) => (
                  <p key={i} className="m-0 mb-4 last:mb-0 text-gray-700">
                    {p}
                  </p>
                ));
              })()}
            </div>

            {Array.isArray(activeParamSheet?.texts?.sources) && activeParamSheet.texts.sources.length > 0 ? (
              <div className="mt-5 pt-4 border-t border-neutral-300/40">
                <p className="m-0 mb-2 text-sm font-semibold text-gray-800">{t('Källor')}:</p>
                <ul className="m-0 p-0 list-none space-y-2">
                  {activeParamSheet.texts.sources.map((src, idx) => (
                    <li key={`${src?.url || src?.name || 'src'}-${idx}`}>
                      <a
                        href={src?.url || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-gray-700 underline break-words"
                      >
                        {src?.name || src?.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      </MobileBottomSheet>
    </div>
  );
};

export default PublicDishInfoPage;
